import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';

import { SolicitationAttachmentsField } from '@/presentation/pages/developments/solicitations/solicitation-attachments-field.js';

export type NewSolicitationPayload = {
    type: 'BUG' | 'AJUSTE_MINOR';
    title: string;
    description: string;
    stepsToReproduce?: string;
    files: File[];
};

interface NewSolicitationDialogLabels {
    newRequest: string;
    requestType: string;
    requestTitle: string;
    requestDescription: string;
    requestSteps: string;
    cancel: string;
    submit: string;
    submittingRequest: string;
    attachments: string;
    attachmentsHint: string;
    addFiles: string;
    invalidFileFormat: (files: string) => string;
    fileTooLarge: (files: string) => string;
    tooManyFiles: string;
}

interface NewSolicitationDialogProps {
    open: boolean;
    submitting: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (payload: NewSolicitationPayload) => void;
    labels: NewSolicitationDialogLabels;
    typeLabels: { BUG: string; AJUSTE_MINOR: string };
}

export function NewSolicitationDialog({ open, submitting, error, onClose, onSubmit, labels, typeLabels }: NewSolicitationDialogProps) {
    const [type, setType] = useState<'BUG' | 'AJUSTE_MINOR' | ''>('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState('');
    const [files, setFiles] = useState<File[]>([]);

    useEffect(() => {
        if (open) {
            setType('');
            setTitle('');
            setDescription('');
            setSteps('');
            setFiles([]);
        }
    }, [open]);

    const isValid = Boolean(type) && title.trim().length > 0 && description.trim().length > 0;

    const handleSubmit = () => {
        if (!isValid || !type) {
            return;
        }
        onSubmit({ type, title, description, stepsToReproduce: steps.trim() || undefined, files });
    };

    const handleClose = () => {
        if (!submitting) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={submitting} maxWidth="xs" fullWidth data-testid="new-solicitation-dialog">
            <DialogTitle>{labels.newRequest}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField select label={labels.requestType} value={type} onChange={(e) => setType(e.target.value as 'BUG' | 'AJUSTE_MINOR')} size="small" fullWidth>
                        <MenuItem value="BUG">{typeLabels.BUG}</MenuItem>
                        <MenuItem value="AJUSTE_MINOR">{typeLabels.AJUSTE_MINOR}</MenuItem>
                    </TextField>
                    <TextField label={labels.requestTitle} value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth />
                    <TextField label={labels.requestDescription} value={description} onChange={(e) => setDescription(e.target.value)} size="small" multiline rows={3} fullWidth />
                    <TextField label={labels.requestSteps} value={steps} onChange={(e) => setSteps(e.target.value)} size="small" multiline rows={2} fullWidth />
                    <SolicitationAttachmentsField files={files} onFilesChange={setFiles} disabled={submitting} labels={labels} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} data-testid="new-solicitation-cancel">
                    {labels.cancel}
                </Button>
                <Button onClick={handleSubmit} variant="contained" sx={{ color: 'white' }} disabled={submitting || !isValid} data-testid="new-solicitation-submit">
                    {submitting ? labels.submittingRequest : labels.submit}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
